import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";
import BottomNav from "../components/BottomNav";
import { getUser } from "../utils/storage";
import { useToast } from "../components/GlobalToast";

const Withdrawal = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const storedUser = getUser();
    const [balance, setBalance] = useState(0);
    const [amount, setAmount] = useState("");
    const [bankDetails, setBankDetails] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const balRes = await API.get(`/wallet/balance?userId=${storedUser.id}`);
            setBalance(parseFloat(balRes.data.balance));

            const bankRes = await API.get(`/wallet/bank-details?userId=${storedUser.id}`);
            setBankDetails(bankRes.data.bankDetails);
        } catch (err) {
            console.error(err);
        }
    };

    const handleWithdraw = async (e) => {
        e.preventDefault();
        if (!bankDetails) return toast.warn("Please add bank details first!");
        if (parseFloat(amount) < 500) return toast.warn("Minimum withdrawal amount is ₹500");
        if (parseFloat(amount) > balance) return toast.error("Insufficient balance");

        setLoading(true);
        try {
            await API.post("/wallet/withdraw", {
                userId: storedUser.id,
                amount: parseFloat(amount)
            });
            toast.success("Withdrawal request submitted! It will be processed in 24-48 hours. 🏦");
            navigate("/wallet");
        } catch (err) {
            toast.error(err.response?.data?.error || "Withdrawal failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-theme-bg text-theme-text min-h-screen pb-20 font-sans">
            <div className="p-4 bg-theme-card border-b border-theme-border flex items-center sticky top-0 z-50">
                <button 
                  onClick={() => navigate(-1)} 
                  className="w-10 h-10 flex items-center justify-center bg-theme-bg rounded-xl border border-theme-border text-theme-text transition-all active:scale-95 shadow-theme-sm"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                <h2 className="font-bold text-lg text-theme-text tracking-tight uppercase flex-1 text-center pr-10">Withdraw</h2>
            </div>

            <div className="p-6 space-y-8">
                <div className="bg-theme-card p-8 rounded-theme border border-theme-border shadow-theme-sm text-center">
                    <p className="text-theme-text-sec text-[10px] font-bold uppercase tracking-widest mb-3 opacity-60">Withdrawable Balance</p>
                    <p className="text-4xl font-bold text-theme-text">₹{balance.toFixed(2)}</p>
                </div>

                {!bankDetails ? (
                    <div className="bg-red-500/5 border border-red-500/10 p-8 rounded-theme text-center">
                        <p className="text-red-500 text-xs font-bold uppercase mb-4 tracking-tight">Add bank details to withdraw</p>
                        <button
                            onClick={() => navigate("/bank-details")}
                            className="bg-red-500 text-white px-8 py-3 rounded-xl text-xs font-bold uppercase transition active:scale-95 shadow-sm"
                        >
                            Add Bank Details
                        </button>
                    </div>
                ) : (
                    <div className="bg-theme-card p-5 rounded-theme border border-theme-border flex justify-between items-center shadow-theme-sm">
                        <div className="flex flex-col gap-1">
                            <p className="text-[10px] text-theme-text-sec font-bold uppercase opacity-60">Sending to Bank</p>
                            <p className="text-sm font-bold text-theme-text">{bankDetails.bank_name}</p>
                            <p className="text-[11px] text-theme-text-sec font-medium">XXXX{bankDetails.account_number.slice(-4)} | {bankDetails.ifsc}</p>
                        </div>
                        <button
                            onClick={() => navigate("/bank-details")}
                            className="text-accent text-[10px] font-bold uppercase px-3 py-1.5 bg-theme-bg rounded-lg border border-theme-border"
                        >
                            Change
                        </button>
                    </div>
                )}

                <form onSubmit={handleWithdraw} className="space-y-6">
                    <div>
                        <label className="text-[10px] text-theme-text-sec font-bold uppercase block mb-2 px-1">Enter Amount (min ₹500)</label>
                        <div className="relative">
                            <span className="absolute left-6 top-1/2 -translate-y-1/2 text-2xl font-bold text-accent">₹</span>
                            <input
                                type="number"
                                className="w-full bg-theme-bg border border-theme-border rounded-xl py-5 pl-12 pr-6 text-2xl font-bold focus:border-accent outline-none transition placeholder:text-theme-text-sec/30"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder="0"
                                required
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading || !bankDetails}
                        className={`w-full py-4 rounded-xl font-bold text-xs uppercase tracking-widest transition active:scale-95 shadow-sm ${loading || !bankDetails ? "bg-theme-bg-sec text-theme-text-sec opacity-50 cursor-not-allowed border border-theme-border" : "bg-accent text-white hover:brightness-105"}`}
                    >
                        {loading ? "Processing..." : "Confirm Withdrawal"}
                    </button>
                </form>

                <div className="p-5 bg-theme-card rounded-theme border border-theme-border shadow-theme-sm">
                    <h4 className="text-[10px] text-theme-text-sec font-bold uppercase mb-3 opacity-60 tracking-wider">Withdrawal Rules</h4>
                    <ul className="text-[10px] space-y-2 text-theme-text-sec leading-relaxed">
                        <li className="flex items-start gap-2">
                           <span className="mt-1 w-1 h-1 bg-accent rounded-full shrink-0"></span>
                           <span>Minimum withdrawal limit is ₹500</span>
                        </li>
                        <li className="flex items-start gap-2">
                           <span className="mt-1 w-1 h-1 bg-accent rounded-full shrink-0"></span>
                           <span>Maximum daily limit is ₹10,000</span>
                        </li>
                        <li className="flex items-start gap-2">
                           <span className="mt-1 w-1 h-1 bg-accent rounded-full shrink-0"></span>
                           <span>Amount will be credited within 24-48 business hours</span>
                        </li>
                        <li className="flex items-start gap-2">
                           <span className="mt-1 w-1 h-1 bg-accent rounded-full shrink-0"></span>
                           <span>TDS of 30% will be applicable on net winnings as per Govt rules</span>
                        </li>
                    </ul>
                </div>
            </div>

            <BottomNav />
        </div>
    );
};

export default Withdrawal;
